import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ApiErrorCode, offerVersionEndpoints, parseApiError } from '@ooh/contracts';
import type {
  LockReservationsRequest,
  LockReservationsResponse,
  RejectVersionResponse,
  ReleaseRejectedUnitsResponse,
  RemoveRejectedUnitsResponse,
} from '@ooh/contracts';
import { toast } from '@ooh/ui/components/sonner';
import { apiQueryKey } from '../../../data/use-api-suspense-query';
import { useApiClient } from '../../../plugs/api-client';
import { apiErrorToUserMessage } from '../../../lib/api-error-messages';
import { RESERVATION_LIST_KEY_PREFIX } from '../../../reservations/api/queries';
import { offerDetailQueryKey, offerUnitsQueryKeyPrefix } from '../../lib/offer-query-keys';
import { offerVersionsQueryKey, unitDecisionsQueryKey } from './queries';

interface PublishVersionVars {
  changeDescription?: string;
  updatedAt: Date;
}

/**
 * Errors are thrown so the dialog can surface 409/422 inline and stay open; every
 * other status is toasted here.
 */
export function usePublishVersion(offerId: string) {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, PublishVersionVars>({
    mutationFn: ({ changeDescription, updatedAt }) =>
      api.call(offerVersionEndpoints.publish, {
        id: Number(offerId),
        ...(changeDescription !== undefined ? { changeDescription } : {}),
        updatedAt: updatedAt.toISOString(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: offerVersionsQueryKey(offerId) });
      void queryClient.invalidateQueries({ queryKey: offerDetailQueryKey(offerId) });
      // Clears a "not published yet" 404 cached before the first publish (client-view-404-after-publish).
      void queryClient.invalidateQueries({
        queryKey: apiQueryKey(offerVersionEndpoints.clientView, { id: Number(offerId) }),
      });
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      if (parsed.statusCode === 409 || parsed.statusCode === 422) return;
      toast.error(apiErrorToUserMessage(parsed, 'mutation'));
    },
  });
}

interface RejectVersionVars {
  offerId: string;
  versionId: number;
  reason?: string;
}

/**
 * Staff-only (offer-versioning-diff / Version-Specific Rejection): records a rejection on a
 * PUBLISHED version, or withdraws an ACCEPTED one and releases the reservations it produced.
 * A 409 is left to the caller, which shows the superseded message inline.
 */
export function useRejectVersion() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<RejectVersionResponse, Error, RejectVersionVars>({
    mutationFn: ({ offerId, versionId, reason }) =>
      api.call(offerVersionEndpoints.reject, { id: Number(offerId), versionId, reason }),
    onSuccess: async (_data, { offerId }) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: apiQueryKey(offerVersionEndpoints.clientView, { id: Number(offerId) }),
        }),
        queryClient.invalidateQueries({ queryKey: offerDetailQueryKey(offerId), exact: true }),
        queryClient.invalidateQueries({ queryKey: offerVersionsQueryKey(offerId) }),
        queryClient.invalidateQueries({ queryKey: RESERVATION_LIST_KEY_PREFIX }),
      ]);
    },
  });
}

interface RemoveRejectedUnitsVars {
  offerId: string;
  versionId: number;
}

/**
 * Per-unit outcomes come back in the response arrays, not as errors. Removing a unit also frees
 * its hold in the same transaction, so the offer's units list is invalidated with the rest
 * (client-offer-decision-persistence / "Manual removal of rejected units from the offer draft").
 */
export function useRemoveRejectedUnits() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<RemoveRejectedUnitsResponse, Error, RemoveRejectedUnitsVars>({
    mutationFn: ({ offerId, versionId }) =>
      api.call(offerVersionEndpoints.removeRejectedUnits, {
        offerId: Number(offerId),
        versionId,
      }),
    onSuccess: (_data, { offerId, versionId }) => {
      void queryClient.invalidateQueries({ queryKey: offerVersionsQueryKey(offerId) });
      void queryClient.invalidateQueries({ queryKey: unitDecisionsQueryKey(offerId, versionId) });
      void queryClient.invalidateQueries({ queryKey: RESERVATION_LIST_KEY_PREFIX });
      void queryClient.invalidateQueries({ queryKey: offerUnitsQueryKeyPrefix(Number(offerId)) });
    },
    onError: (error) => {
      toast.error(apiErrorToUserMessage(parseApiError(error), 'mutation'));
    },
  });
}

interface LockReservationsVars extends LockReservationsRequest {
  offerId: string;
  versionId: number;
}

export function handleLockReservationsError(error: unknown, t: (key: string) => string): void {
  const parsed = parseApiError(error);
  if (parsed.errorCode === ApiErrorCode.OFFER_VERSION_NOT_ACCEPTED) {
    toast.error(t('workspace.versions.lock.toasts.notAccepted'));
    return;
  }
  toast.error(apiErrorToUserMessage(parsed, 'mutation'));
}

/**
 * Per-window outcomes come back in the locked/queued/failed arrays, not as errors; the call only
 * rejects on a whole-request failure (e.g. the version is no longer ACCEPTED).
 */
export function useLockReservations() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { t } = useTranslation('offers');

  return useMutation<LockReservationsResponse, Error, LockReservationsVars>({
    mutationFn: ({ offerId, versionId, status, ttlExpiresAt }) =>
      api.call(offerVersionEndpoints.lockReservations, {
        offerId: Number(offerId),
        versionId,
        status,
        ttlExpiresAt,
      }),
    onSuccess: (_data, { offerId, versionId }) => {
      void queryClient.invalidateQueries({ queryKey: offerVersionsQueryKey(offerId) });
      void queryClient.invalidateQueries({ queryKey: unitDecisionsQueryKey(offerId, versionId) });
      void queryClient.invalidateQueries({ queryKey: RESERVATION_LIST_KEY_PREFIX });
    },
    onError: (error) => handleLockReservationsError(error, t as (key: string) => string),
  });
}

interface ReleaseRejectedUnitsVars {
  offerId: string;
  versionId: number;
}

/**
 * Frees holds only and never touches the draft units, so unlike `useRemoveRejectedUnits` it does
 * not invalidate the offer's units list.
 */
export function useReleaseRejectedUnits() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<ReleaseRejectedUnitsResponse, Error, ReleaseRejectedUnitsVars>({
    mutationFn: ({ offerId, versionId }) =>
      api.call(offerVersionEndpoints.releaseRejectedUnits, {
        offerId: Number(offerId),
        versionId,
      }),
    onSuccess: (_data, { offerId, versionId }) => {
      void queryClient.invalidateQueries({ queryKey: offerVersionsQueryKey(offerId) });
      void queryClient.invalidateQueries({ queryKey: unitDecisionsQueryKey(offerId, versionId) });
      void queryClient.invalidateQueries({ queryKey: RESERVATION_LIST_KEY_PREFIX });
    },
    onError: (error) => {
      toast.error(apiErrorToUserMessage(parseApiError(error), 'mutation'));
    },
  });
}
